<template>
	<div :class="['fixed-top', { 'fixed-top-underline': type === 'threadRooms' }]">
		<div :class="['header', { 'header-logo-only': type === 'home' && !showButtons }]">
			<base-button
				icon-only
				v-if="showButtons"
				:class="{ 'header-btn-guest': guestAccent }"
				:icon="leftButtonIcon"
				@click="handleLeftButtonClick"
			/>

			<div @click="logoClickHandler">
				<img :src="appLogoPath" :alt="logoAlt" width="134" />
			</div>

			<base-button
				icon-only
				v-if="showButtons"
				:class="{ 'header-btn-guest': guestAccent, 'header-btn-login-cross': isLoginPage }"
				:icon="rightButtonIcon"
				@click="handleRightButtonClick"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import BaseButton from '@/components/base/BaseButton.vue'
import { useAuthStore } from '@/stores/auth'
import { useCommonStore } from '@/stores/common'
import { redirectToAuth } from '@/utils/authRedirect'
import { brandName } from '@/config/tenant'
import AppConfig from '@/config'

const props = defineProps({
	type: { type: String }
})

declare const window: Window
const authStore = useAuthStore()
const commonStore = useCommonStore()

const emit = defineEmits<{
	closePage: []
}>()

const userAuth = computed(() => authStore.isAuthenticated)
// В internal-режиме гость остаётся в приложении, поэтому кнопки ему показываем —
// но акцентным цветом и с приглашением войти вместо навигации.
const isGuestInternal = computed(() => !userAuth.value && AppConfig.authMode === 'internal')
// Экраны входа и онбординга: одинаковая шапка — декоративная иконка профиля слева, крестик справа.
const isLoginPage = computed(() => props.type === 'login' || props.type === 'onboarding')
// На экранах входа кнопки нужны и гостю: справа крестик — выход на главную.
const showButtons = computed(() => isLoginPage.value || userAuth.value || isGuestInternal.value)
// Акцентный гостевой цвет — приглашение войти; на самих экранах входа он не нужен.
const guestAccent = computed(() => isGuestInternal.value && !isLoginPage.value)
const leftButtonIcon = computed(() => (props.type === 'profile' || isLoginPage.value ? 'profile' : 'thread'))
const rightButtonIcon = computed(() => (props.type === 'home' ? 'profile' : 'cross'))
const appLogoPath = computed(() => import.meta.env.VITE_APP_LOGO_PATH || '/logo.png')
const logoAlt = brandName()

const router = useRouter()

function handleLeftButtonClick() {
	if (isLoginPage.value) return // иконка профиля на экране входа декоративная
	if (isGuestInternal.value) return commonStore.openLoginModal()
	if (props.type === 'home') goToThread()
}

function handleRightButtonClick() {
	// Онбординг уходит с экрана сам: сначала отмечает прохождение, потом навигирует.
	if (props.type === 'onboarding') return emit('closePage')
	if (isLoginPage.value) return router.push('/')
	if (isGuestInternal.value) return commonStore.openLoginModal()
	if (props.type === 'home') {
		goToProfile()
	} else {
		if (props.type === 'threadRooms') {
			emit('closePage')
			return
		}
		if (props.type === 'profile') return router.push('/')
		exit()
	}
}

function exit() {
	window.history.back()
}
function goToThread() {
	router.push('/thread-rooms')
}
function goToProfile() {
	router.push('/profile')
}

function logoClickHandler() {
	if (userAuth.value) {
		router.push('/')
	} else if (!redirectToAuth()) {
		router.push('/')
	}
}
</script>

<style lang="scss" scoped>
.fixed-top {
	position: fixed;
	top: -1px;
	left: 0;
	right: 0;
	width: 100%;
	margin: 0 auto;
	background: var(--background-primary);
	z-index: 200;
	&-underline {
		border: 1px solid var(--background-secondary);
	}
}

.header {
	@include flex-space-between;
	width: 100%;
	padding: $padding-container;
	padding-top: 12px;
	padding-bottom: 24px;
	max-width: $max-content-width;
	margin: 0 auto;
}

.header-title {
	font: var(--font-heading);
	color: var(--text-inverse);
	width: 100%;
	&-right {
		text-align: right;
		padding-right: 16px;
	}
	&-left {
		text-align: left;
		padding-left: 16px;
	}
}

.header-logo-only {
	@include flex-center;
}

// Глиф cross заполняет весь viewBox 24×24, поэтому на экранах входа и онбординга уменьшаем его
// до 12px. Пропы size/iconSize на это не влияют: SvgIcon отдаёт их числом в :style, и браузер
// такое значение игнорирует — размер задаётся только через CSS.
.header .header-btn-login-cross :deep(.svg-icon) {
	width: 12px;
	height: 12px;
}

// Вложенность под .header — для специфичности: перебиваем color: var(--text-primary)
// из .button-icon в BaseButton.vue.
.header .header-btn-guest {
	color: var(--text-brand);
	&:hover {
		color: var(--text-brand);
	}
	&:active {
		color: var(--text-brand);
	}
}
</style>
